'use client';

import React from 'react';
import { WatchlistMixConfig } from './utils';

const ReactECharts = React.lazy(() => import('echarts-for-react'));

const CHART_SIZE = 92;

const DonutPlaceholder = () => (
  <div className="relative size-[92px] shrink-0">
    <div className="absolute inset-0 animate-pulse rounded-full bg-white/10" />
    <div className="absolute inset-[30%] rounded-full bg-bzblue-800" />
  </div>
);

interface WatchlistMixProps {
  configs: WatchlistMixConfig[];
  isLoading?: boolean;
  variant?: 'embedded' | 'standalone' | 'cell';
}

const MixLegend = ({
  items,
  total,
}: {
  items: WatchlistMixConfig['breakdown'];
  total: number;
}) => {
  const useTwoColumns = items.length > 4;

  return (
    <ul
      className={`grid min-w-0 flex-1 gap-x-2 gap-y-1 ${useTwoColumns ? 'grid-cols-2' : 'grid-cols-1'}`}
    >
      {items.map(item => {
        const share = total > 0 ? Math.round((item.value / total) * 100) : 0;

        return (
          <li key={item.name} className="flex min-w-0 items-center gap-1.5 text-xs leading-tight">
            <span className="size-2 shrink-0 rounded-sm" style={{ backgroundColor: item.color }} />
            <span className={`min-w-0 flex-1 text-white/90 ${useTwoColumns ? 'truncate' : ''}`}>
              {item.name}
            </span>
            <span className="shrink-0 tabular-nums text-slate-400">{share}%</span>
          </li>
        );
      })}
    </ul>
  );
};

const MixChartSkeleton = ({ label }: { label: string }) => (
  <div className="flex h-full min-h-[7.5rem] flex-col gap-2 bg-bzblue-800 p-3">
    <p className="text-xs font-medium text-slate-300">{label}</p>
    <div className="flex flex-1 items-center gap-2.5">
      <DonutPlaceholder />
      <ul className="grid min-w-0 flex-1 grid-cols-2 gap-x-2 gap-y-1">
        {Array.from({ length: 6 }).map((_, index) => (
          <li key={index} className="flex items-center gap-1.5">
            <span className="size-2 shrink-0 animate-pulse rounded-sm bg-white/20" />
            <span className="h-3 min-w-0 flex-1 animate-pulse rounded bg-white/15" />
          </li>
        ))}
      </ul>
    </div>
  </div>
);

export const MixChartCard = ({ config }: { config: WatchlistMixConfig }) => {
  const total = config.breakdown.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="flex h-full min-h-[7.5rem] flex-col gap-2 bg-bzblue-800 p-3">
      <p className="text-xs font-medium text-slate-300">{config.name}</p>
      <div className="flex flex-1 items-center gap-2.5">
        <React.Suspense fallback={<DonutPlaceholder />}>
          <ReactECharts
            option={config.option}
            style={{ height: CHART_SIZE, width: CHART_SIZE }}
            opts={{ renderer: 'svg' }}
          />
        </React.Suspense>
        <MixLegend items={config.breakdown} total={total} />
      </div>
    </div>
  );
};

export const WatchlistMix = ({ configs, isLoading = false, variant = 'embedded' }: WatchlistMixProps) => {
  const cards = isLoading ? (
    <>
      <MixChartSkeleton label="Sectors" />
      <MixChartSkeleton label="Regions" />
    </>
  ) : configs.length === 0 ? null : (
    configs.map(config => <MixChartCard key={config.name} config={config} />)
  );

  if (variant === 'cell') {
    if (configs.length === 0 && !isLoading) {
      return (
        <p className="col-span-full bg-bzblue-800 px-4 py-4 text-center text-sm text-white/80">
          Add more equity tickers to see sector and region mix.
        </p>
      );
    }

    return <>{cards}</>;
  }

  if (variant === 'standalone') {
    return (
      <div className="rounded-b-md border-t border-white/10 bg-bzblue-800 text-white">
        {configs.length === 0 && !isLoading ? (
          <p className="px-4 py-4 text-center text-sm text-white/80">
            Add more equity tickers to see sector and region mix.
          </p>
        ) : (
          <div className="grid gap-px bg-white/10 sm:grid-cols-2">{cards}</div>
        )}
      </div>
    );
  }

  if (configs.length === 0 && !isLoading) {
    return (
      <p className="border-t border-white/10 px-4 py-4 text-center text-sm text-white/80">
        Add more equity tickers to see sector and region mix.
      </p>
    );
  }

  return <div className="border-t border-white/10">{cards}</div>;
};
